import {DataFetcher, useGrpcRequest} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {SearchService_GetResolvedTermLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/search/v1/service.grpc.lazy';
import {GetResolvedTermResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/search/v1/service.types';
import type {SearchTypeTypes} from '@aboutyou/legacy-tracking-sdk';
import {
  Redirect,
  RouterErrorHandler,
  createRedirectHint,
  useParams,
  usePathname,
  useSearch,
  useSearchRoute,
} from '@aboutyou/router';
import {
  useSearchEventsUtils,
  useTrackMessage,
  useTrackSearch,
} from '@aboutyou/tracking';
import {
  ErrorPageInline,
  LoadingPageSpinner,
  useShowToast,
} from '@aboutyou/ui-components';
import {useFullSession} from '@aboutyou/user';
import {doAsync} from '@aboutyou/utils';
import React, {FC, createContext, useContext} from 'react';
import {useUpdateTargetGroup} from '../../shared-deprecated/useSelectedTargetGroup';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';
import {useSearchTerm} from './hooks/useSearchTerm';

interface SearchPageResolverProps {
  SearchPage: React.ReactNode;
  SearchMaskPage?: React.ReactNode;
}

export const SearchPageResolver = ({
  SearchPage,
  SearchMaskPage,
}: SearchPageResolverProps) => {
  const paramsTerm = useParams<{term?: string}>().term;
  const searchRoute = useSearchRoute();
  const queryTerm = useSearchTerm();

  if (paramsTerm) {
    return <Redirect to={`${searchRoute}?term=${paramsTerm}`} />;
  }

  if (!queryTerm) {
    if (SearchMaskPage) {
      return <>{SearchMaskPage}</>;
    }
    return <Redirect to={'/'} statusCode={301} />;
  }

  return <ResolveSearchTerm SearchPage={SearchPage} term={queryTerm} />;
};

const ResolveSearchTerm: FC<
  {
    SearchPage: React.ReactNode;
  } & {term: string}
> = ({SearchPage, term}) => {
  return (
    <DataFetcher
      request={useGetResolvedTerm(term)}
      Error={props => (
        <RouterErrorHandler {...props}>
          <ErrorPageInline />
        </RouterErrorHandler>
      )}
      viewProps={{SearchPage}}
      View={props => {
        if (props.result?.$case === 'noResult') {
          const {searchToken, recommendedTargetGroup} = props.result.noResult;
          return (
            <SearchTokenContext.Provider value={searchToken}>
              <TargetGroupUpdater
                recommendedTargetGroup={recommendedTargetGroup}
              />
              {props.SearchPage}
            </SearchTokenContext.Provider>
          );
        }
        return null;
      }}
      Loading={() => <LoadingPageSpinner />}
    />
  );
};

const useGetResolvedTerm = (term: string) => {
  const targetGroup = useTargetGroupIdentifier();

  const toast = useShowToast();

  const isSemanticSearchEnabled = useIsFeatureActive(
    'enable_semantic_search',
  ).isActive;
  const {isActive: isSearchAbTestActive, activateAbTest: activateSearchAbTest} =
    useIsFeatureActive('search_internationalization');

  const trackSearch = useTrackSearch();
  const {searchEventContextParams, resetEventContextParams} =
    useSearchEventsUtils();

  const fullSession = useFullSession();

  const search = useSearch();
  const pathname = usePathname();

  const trackMessage = useTrackMessage();

  const handleMessage = (data: GetResolvedTermResponse) => {
    const message =
      data.result?.$case === 'noResult'
        ? data.result.noResult.message
        : data.result?.resolvedUrl.message;
    if (message) {
      toast({
        content: message,
        type: 'INFO',
      });
      doAsync(() => {
        trackMessage({
          action: 'impression',
          content_type: 'banner',
          name: 'search_toast',
          event_scope: 'long',
          context: 'missing_entity',
        });
      });
    }
  };

  const validateData = (data: GetResolvedTermResponse) => {
    const searchType = data.searchTracker.searchType as SearchTypeTypes;

    const searchDestination =
      data.result?.$case === 'resolvedUrl'
        ? data.result.resolvedUrl.url
        : `${pathname}${search}`;

    // Guard on `searchTerm` (not just the object): the ref-backed context is
    // reset to empty strings — not null — after tracking, so `cacheTime: 0`
    // refetches on remount (e.g. history-back from ADP) would otherwise
    // fire a duplicate empty submit event.
    if (searchEventContextParams?.searchTerm) {
      const {searchName, searchTerm, searchTermCompleted} =
        searchEventContextParams;

      // Activation Event (ASFE-14251): the customer submitted a search, so
      // record the `search_internationalization` exposure for both control and variant.
      activateSearchAbTest();

      trackSearch({
        searchAction: 'submit',
        searchType:
          searchType === 'semantic_search' &&
          !isSemanticSearchEnabled &&
          !isSearchAbTestActive
            ? 'srp'
            : searchType,
        searchTerm,
        searchTermCompleted,
        searchName,
        searchDestination,
        searchId: data.searchTracker.searchId,
        searchVersion: data.searchTracker.searchVersion,
      });
      resetEventContextParams();
    }

    handleMessage(data);

    if (data.result?.$case === 'resolvedUrl') {
      const resolvedUrl = data.result?.resolvedUrl;
      throw createRedirectHint(resolvedUrl.url, resolvedUrl.statusCode);
    }
  };

  return useGrpcRequest(
    SearchService_GetResolvedTermLazy,
    {
      targetGroup,
      term,
      session: fullSession,
    },
    {
      validateData,
      cacheTime: 0,
    },
  );
};

const TargetGroupUpdater: FC<{
  recommendedTargetGroup: TargetGroup | undefined;
}> = ({recommendedTargetGroup}) => {
  useUpdateTargetGroup(recommendedTargetGroup);
  return null;
};

const SearchTokenContext = createContext<string | undefined>(undefined);

export const useSearchToken = () => {
  const token = useContext(SearchTokenContext);
  if (!token) {
    throw new Error(
      'useSearchToken must be used within a SearchTokenContext.Provider',
    );
  }
  return token;
};
